// services/benchmarkService.js — SharePoint CRUD for SCMBenchmarkDB list
import { getSiteId, fetchAllPages, GRAPH_PAGE_SIZE } from "./sharepointService";

const graphBase = "https://graph.microsoft.com/v1.0";
const BENCHMARK_LIST = "SCMBenchmarkDB";

export const BENCHMARK_CATEGORIES = [
  "Logistics", "Well Engineering", "EPC / Projects", "Operations",
  "IT", "Technical Consultancy", "Fuel Supply",
  "Catering", "Security", "Medical Services", "Professional Services",
  "Office Facilities", "Equipment Rental", "Other",
];

export const CATEGORY_PHASE = {
  "Logistics": 1, "Well Engineering": 1, "EPC / Projects": 1,
  "Operations": 1, "IT": 1, "Technical Consultancy": 1, "Fuel Supply": 1,
  "Catering": 2, "Security": 2, "Medical Services": 2,
  "Professional Services": 2, "Office Facilities": 2, "Equipment Rental": 2,
  "Other": 3,
};

export const PHASE_LABELS = {
  1: "Phase 1 — High Value",
  2: "Phase 2 — Mid Value",
  3: "Phase 3 — Low Value",
};

function spToBenchmark(fields) {
  return {
    id:                  fields.id || fields.ID,
    tenderRef:           fields.Title || "",
    category:            fields.Category || "Other",
    location:            fields.Location || "",
    commodityGroup:      fields.CommodityGroup || "",
    serviceDescription:  fields.ServiceDescription || "",
    supplierName:        fields.SupplierName || "",
    unitOfMeasure:       fields.UnitOfMeasure || "",
    benchmarkRate:       parseFloat(fields.BenchmarkRate)     || 0,
    lowestRate:          parseFloat(fields.LowestRate)        || 0,
    awardedRate:         parseFloat(fields.AwardedRate)       || 0,
    percentageChange:    parseFloat(fields.PercentageChange)  || 0,
    currency:            fields.Currency || "NGN",
    commercialTerms:     fields.CommercialTerms || "",
    awardJustification:  fields.AwardJustification || "",
    tenderDate:          fields.TenderDate ? fields.TenderDate.slice(0, 10) : "",
    contractDuration:    fields.ContractDuration || "",
    costSavingAchieved:  parseFloat(fields.CostSavingAchieved) || 0,
    marketObservation:   fields.MarketObservation || "",
    phase:               parseInt(fields.Phase) || 3,
    submittedBy:         fields.SubmittedBy || "",
    submittedByName:     fields.SubmittedByName || "",
    createdAt:           fields.Created  || new Date().toISOString(),
    updatedAt:           fields.Modified || new Date().toISOString(),
    _spItemId:           fields.id || fields.ID,
  };
}

function benchmarkToSp(record) {
  return {
    Title:               record.tenderRef,
    Category:            record.category || "Other",
    Location:            record.location || "",
    CommodityGroup:      record.commodityGroup || "",
    ServiceDescription:  record.serviceDescription || "",
    SupplierName:        record.supplierName || "",
    UnitOfMeasure:       record.unitOfMeasure || "",
    BenchmarkRate:       record.benchmarkRate || 0,
    LowestRate:          record.lowestRate || 0,
    AwardedRate:         record.awardedRate || 0,
    PercentageChange:    record.percentageChange || 0,
    Currency:            record.currency || "NGN",
    CommercialTerms:     record.commercialTerms || "",
    AwardJustification:  record.awardJustification || "",
    TenderDate:          record.tenderDate || null,
    ContractDuration:    record.contractDuration || "",
    CostSavingAchieved:  record.costSavingAchieved || 0,
    MarketObservation:   record.marketObservation || "",
    Phase:               String(record.phase || 3),
    SubmittedBy:         record.submittedBy || "",
    SubmittedByName:     record.submittedByName || "",
  };
}

export async function fetchBenchmarks(token) {
  const siteId = await getSiteId(token);
  const raw = await fetchAllPages(
    token,
    `${graphBase}/sites/${siteId}/lists/${BENCHMARK_LIST}/items?expand=fields&$top=${GRAPH_PAGE_SIZE}`,
    { errorMessage: "Failed to fetch benchmark records" },
  );
  const items = raw.map((i) => spToBenchmark({ ...i.fields, id: i.id }));
  return items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export async function createBenchmark(token, record) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${BENCHMARK_LIST}/items`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ fields: benchmarkToSp(record) }),
    },
  );
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error?.message || "Failed to create benchmark record");
  }
  const item = await res.json();
  return spToBenchmark({ ...item.fields, id: item.id });
}

export async function updateBenchmark(token, spItemId, record) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${BENCHMARK_LIST}/items/${spItemId}`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ fields: benchmarkToSp(record) }),
    },
  );
  if (!res.ok) throw new Error("Failed to update benchmark record");
}

export async function deleteBenchmark(token, spItemId) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${BENCHMARK_LIST}/items/${spItemId}`,
    { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error("Failed to delete benchmark record");
}
